<template>
	<teleport v-if="shouldShowPopup" to="#click-wrapper">
		<div
			class="popup-wrapper"
			:class="[
				`popup-wrapper--${computedType}`,
				{
					'pointer-events-none': !isClickable,
					'max-z-index': computedMaxZIndex,
				},
			]"
		>
			<div
				v-if="motionVideoPath"
				class="video-footage-wrapper"
				:class="`video-footage-wrapper--${computedType}`"
			>
				<video
					ref="video"
					class="video-footage"
					autoplay
					loop
					muted
					playsinline
					poster="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAACXBIWXMAAArEAAAKxAFmbYLUAAAADUlEQVQImWP4//8/AwAI/AL+hc2rNAAAAABJRU5ErkJggg=="
				>
					<!-- If the browser's video player is unable to play the MP4 file, nothing will be rendered except the poster image (transparent  1x1px). -->
					<source type="video/mp4" :src="motionVideoPath" />
				</video>
			</div>
			<div
				ref="containerRef"
				class="popup"
				:class="[
					`popup--${computedSkin} popup--${computedType} ${customClassList ?? ''}`,
					{ 'popup--overlapped': computedHasOverlap },
				]"
				:data-event-type="eventAttributes.type"
				:style="{
					width: computedPercentageWidth
						? computedPercentageWidth
						: computedWidth + 'rem',
					height: computedMaxHeight
						? 'unset'
						: typeof height === 'string'
							? height
							: computedHeight + 'rem',
					maxHeight: computedMaxHeight
						? computedMaxHeight + 'rem'
						: 'unset',
					maxWidth:
						computedPercentageWidth && computedWidth
							? `${computedWidth}rem`
							: 'none',
				}"
			>
				<div
					class="popup__sprite"
					:class="{
						'popup__sprite--overlapped':
							computedSpriteData?.hasOverlap ?? false,
					}"
				>
					<div
						v-if="computedType === PopupType.EVENT"
						@click="openEventIntro"
						class="event-popup__intro-btn"
						:data-event-type="eventAttributes.type"
					/>
					<EventBestReward
						v-if="
							computedType === PopupType.EVENT &&
							computedSpriteData?.key === MILESTONES_TAB_INDEX
						"
					/>
					<div
						v-if="computedSpriteData && !computedImgSrc"
						class="popup__sprite-img"
						:class="`popup__sprite--${computedSpriteData.slug} popup__sprite--${popupHeaderImgSize} popup__sprite--${computedSpriteData.slug}-${computedSpriteData.key}-${popupHeaderImgSize}`"
					/>
				</div>
				<img
					v-if="computedImgSrc && !computedSpriteData"
					:src="`${pathImages}/${computedImgSrc}`"
					class="popup__img"
				/>
				<video
					class="popup__img"
					v-if="computedType === PopupType.QT"
					autoplay
					loop
					muted
					playsinline
				>
					<source
						:src="`${pathImagesHttp}/popups/qt-video.mp4`"
						type="video/mp4"
					/>
				</video>
				<div v-if="computedDecoratedHeading" class="decorated-header">
					{{ computedDecoratedHeading }}
				</div>
				<div
					v-if="computedHeading"
					class="header"
					:class="{
						'header--has-img': computedImgSrc,
						'header--large': computedLargeHeader,
						'header--fixed-top':
							computedType === PopupType.EVENT ||
							computedType === PopupType.LOCKED_MAP_REGION,
					}"
				>
					<div
						v-if="computedHeadingResource"
						class="header-top-resource"
					>
						<AppMainIcon
							class="header-top-resource__icon"
							:iconName="computedHeadingResource.toLowerCase()"
							:iconSize="72"
						/>
					</div>
					<span
						class="heading"
						:class="{ 'heading--center': !computedCloseable }"
					>
						{{ computedHeading }}
					</span>
					<AppIconButton
						v-if="computedCloseable"
						:class="{ 'icon--has-img': computedImgSrc }"
						:type="IconTypes.CLOSE"
						@click="close"
					/>
					<slot name="header" />
				</div>
				<DividerLine
					v-if="
						((computedSkin === SkinTypes.LIGHT ||
							computedForceDividerLine ||
							computedType === PopupType.NEWS) &&
							computedType !== PopupType.CHEST &&
							computedType !== PopupType.QT &&
							computedHeading) ||
						(computedType === PopupType.EVENT &&
							computedForceDividerLine)
					"
				/>
				<HorizontalDivider
					v-else-if="
						computedSkin !== SkinTypes.PREMIUM &&
						computedType !== PopupType.CHEST &&
						computedType !== PopupType.QT &&
						computedType !== PopupType.NO_DIVIDER &&
						computedHeading
					"
				/>
				<div class="body">
					<div
						class="body-content"
						:class="{
							'body-content--paddingless':
								computedIsPaddingless ||
								computedType === PopupType.CHEST,
							'body-content--center': computedIsCentered,
							'body-content--acc-link-options':
								computedType === PopupType.ACCOUNT_LINK_OPTIONS,
							'body-content--rate-us':
								computedType === PopupType.RATE_US,
							'body-content--delete':
								computedType === PopupType.DELETE,
							'body-content--upgrade':
								computedType === PopupType.UPGRADE,
							'body-content--discord-reward':
								computedType === PopupType.DISCORD_REWARD,
						}"
					>
						<!-- PRIORITY 1: Dynamic component (for Shop, Events, etc.) -->
						<component
							v-if="activePopupComponent"
							:is="activePopupComponent"
							v-bind="activePopupProps"
							@closePopup="close"
						/>

						<!-- PRIORITY 2: Original logic (for simple text info popups) -->
						<template v-else>
							<template
								v-if="
									computedType ===
										PopupType.ACCOUNT_LINK_OPTIONS ||
									popupStore.skin.value === SkinTypes.PREMIUM
								"
							>
								<template
									v-if="
										computedType ===
										PopupType.ACCOUNT_LINK_OPTIONS
									"
								>
									<div
										class="body-content--acc-link-options--content-wrapper"
									>
										<div>
											{{
												translate(
													"ui.linked_account_found",
												)
											}}
										</div>
										<HorizontalDecorDivider
											style="width: 100%"
										/>
										<div class="sub-header">
											{{
												translate("ui.current_account")
											}}
										</div>
										<AccountDetail
											:level="
												userDataOverride?.level ?? 0
											"
											:tilesExplored="
												userDataOverride?.tilesExplored ??
												0
											"
											:coinsIncome="
												userDataOverride?.coinsIncome ??
												0
											"
										/>
										<AppTextButton
											class="link-account__btn"
											:type="StatusTypes.PRIMARY"
											:text="
												translate('button.select') +
												` ${userDataOverride?.name}`
											"
											@click="
												linkAccount(
													LinkTypes.WITH_OVERRIDE,
												)
											"
										/>
										<HorizontalDecorDivider
											style="width: 100%"
										/>
										<div class="sub-header">
											{{ translate("ui.linked_account") }}
										</div>
										<AccountDetail
											:level="
												userDataExisting?.level ?? 0
											"
											:tilesExplored="
												userDataExisting?.tilesExplored ??
												0
											"
											:coinsIncome="
												userDataExisting?.coinsIncome ??
												0
											"
										/>
										<AppTextButton
											class="link-account__btn"
											:type="StatusTypes.PRIMARY"
											:text="
												translate('button.select') +
												` ${userDataExisting?.name}`
											"
											@click="
												linkAccount(
													LinkTypes.WITH_EXIST,
												)
											"
										/>
									</div>
									<div class="link-account-warning">
										{{
											translate("ui.warning_account_loss")
										}}
									</div>
								</template>
								<div
									class="premium-box"
									v-if="
										popupStore.skin.value ===
										SkinTypes.PREMIUM
									"
								>
									<div
										class="premium-box__content"
										:class="{
											'premium-box__content--special-offer':
												isSpecialShopDetail,
										}"
									>
										<span
											v-if="
												(
													computedBody as ComputedBodySpecial
												)?.ctaText
											"
											class="premium-box__text"
										>
											{{
												(
													computedBody as ComputedBodySpecial
												).ctaText
											}}
										</span>
										<span
											v-if="
												(
													computedBody as ComputedBodySpecial
												)?.ctaText2
											"
											class="premium-box__text2"
										>
											{{
												(
													computedBody as ComputedBodySpecial
												).ctaText2
											}}
										</span>
										<div
											v-if="
												(
													computedBody as Record<
														string,
														unknown
													>
												)?.value
											"
										>
											<!-- prettier-ignore -->
											<AppPrice
											v-for="[
												key,
												value,
											] in (Object.entries(
												(
													computedBody as Record<
														string,
														string | number
													>
												).value,
											) as Array<[ResourceType, number]>)"
											:key="key"
											:amount="value"
											:currency="key"
											:resourceCheck="false"
											:resourceChangeSign="
												ResourceChangeHighlight.POSITIVE
											"
											:fontSize="28"
											:iconSize="24"
											:fontWeight="FontWeights.REGULAR"
										/>
										</div>
										<div
											class="premium-box__price"
											v-if="
												(
													computedBody as Record<
														string,
														unknown
													>
												)?.price
											"
										>
											<!-- prettier-ignore -->
											<AppPrice
											v-for="[
												key,
												value,
											] in (Object.entries(
												(
													computedBody as Record<
														string,
														string | number
													>
												).price,
											) as Array<[ResourceType, number]>)"
											:key="key"
											:amount="value"
											:currency="key"
											:resourceCheck="false"
											:fontSize="28"
											:iconSize="24"
											:fontWeight="FontWeights.REGULAR"
										/>
										</div>
									</div>
									<RectangleBoxContainer
										:border="[1, 0, 1, 0]"
									/>
								</div>
							</template>
							<template v-else>
								<ChestPopupBody
									v-if="computedType === PopupType.CHEST"
									:rewards="computedBody as string"
									@close="close"
								/>
								<QTPopupBody
									v-else-if="computedType === PopupType.QT"
									@close="close"
								/>
								<NewsPopupBody
									v-else-if="computedType === PopupType.NEWS"
									:news="computedBody as News"
								/>
								<RulesPopupBody
									v-else-if="computedType === PopupType.RULES"
								/>
								<DailyRewardPopupBody
									v-else-if="
										computedType === PopupType.DAILY_REWARD
									"
									@close="close"
								/>
								<EventPossibleRewardsPopupBody
									:chest-type="
										computedBody as MarketChestType
									"
									v-else-if="
										computedType ===
										PopupType.EVENT_POSSIBLE_REWARDS
									"
									@close="close"
								/>
								<ChangeNamePopupBody
									v-else-if="
										computedType === PopupType.CHANGE_NAME
									"
									@close="close"
								/>
								<!-- prettier-ignore -->
								<LockedMapRegionPopupBody
								v-else-if="
									computedType === PopupType.LOCKED_MAP_REGION
								"
								:body="(computedBody as MapExpansionConfig)"
								@close="close"
							/>
								<div
									v-else-if="
										computedType === PopupType.UPGRADE
									"
								>
									<div class="body-content--texts">
										<div class="upgrade__subheader">
											{{
												(popupStore.type
													.value as PopupType) ===
												PopupType.CHEST
													? ""
													: computedBody
											}}
										</div>
										{{ translate("ui.choose_price") }}
									</div>
								</div>
								<slot v-else name="body">
									<div class="body-content--texts">
										<div
											v-if="
												computedType ===
												PopupType.RATE_US
											"
											class="rate-us"
										>
											<template
												v-if="
													!userStore.appRatedGemRewardClaimed
												"
											>
												<div
													class="rate-us__gems icon-gems-like"
												></div>
												<div class="rate-us__subheader">
													{{
														translate(
															"ui.like_the_game",
														)
													}}
												</div>
											</template>
											<template v-else>
												<div class="rate-us__stars">
													<div
														class="icon-star"
														v-for="i in 5"
														:key="i"
													/>
												</div>
												<div class="rate-us__subheader">
													{{
														translate(
															"ui.thank_you_for_playing",
														)
													}}
												</div>
											</template>
										</div>
										<div
											v-if="
												popupStore.type.value ===
												PopupType.DISCORD_REWARD
											"
											class="discord-reward"
										>
											<div
												class="discord-reward__subheader"
											>
												{{
													translate(
														"ui.join_discord_community",
													)
												}}
											</div>
										</div>
										<div
											v-if="
												computedType ===
												PopupType.DELETE
											"
											class="delete-subheader"
										>
											{{
												translate(
													"ui.delete_account_confirm",
												)
											}}
										</div>
										<span
											v-html="
												(popupStore.type
													.value as PopupType) ===
												PopupType.CHEST
													? ''
													: computedBody
											"
										></span>
									</div>
								</slot>
								<slot name="component" />
							</template>
							<div
								v-if="isSpecialShopDetail"
								class="premium-box__price--outer"
							>
								<span class="price-label">
									{{ translate("ui.price") }}:
								</span>
								<div
									v-if="fiatPrice"
									class="premium-box__price"
								>
									<span class="fiat-price">
										{{
											formatPrice(
												Object.values(
													fiatPrice,
												)[0] as number,
												Object.keys(fiatPrice)[0],
											)
										}}
									</span>
								</div>
							</div>
							<div
								class="popup__skip-message-notice"
								v-if="computedShowSkipMessageNotice"
							>
								<div class="popup__skip-message-notice__row">
									<input
										type="checkbox"
										id="popup__skip-message-notice_label"
										v-model="confirmGemsMessages"
									/>
									<label
										for="popup__skip-message-notice_label"
									>
										{{
											translate(
												"ui.dont_show_this_message",
											)
										}}
									</label>
								</div>
								<div class="popup__skip-message-notice__info">
									{{
										`(${translate("ui.turn_on_message_in_settings")})`
									}}
								</div>
							</div>

							<div
								class="claimable-rewards-wrapper"
								v-if="computedRewards"
							>
								<RaggedBoxBackground
									slug="claimable-rewards-box-bg"
									:skin="{
										stopColor1: '#f2dcb5',
										stopColor2: '#fff3dd',
									}"
								/>
								<div
									class="claimable-rewards"
									v-if="
										computedRewards &&
										Object.keys(computedRewards).length
									"
								>
									<template
										v-for="(
											[reward, value], index
										) in Object.entries(computedRewards)"
										:key="index"
									>
										<div
											v-if="
												value &&
												(value > 0 ||
													typeof value === 'string')
											"
											class="claimable-reward"
										>
											<span
												v-if="
													!Object.values(
														UpgradeIds,
													).includes(+reward) &&
													!Object.values(
														OfferComplementaryReward,
													).includes(
														reward as OfferComplementaryReward,
													)
												"
												class="claimable-reward__value"
											>
												{{
													typeof value === "string"
														? value
														: isMultiplier(
																	reward as ResourceType,
															  )
															? formatNumber(
																	multiplierValue(
																		reward as ResourceType,
																		value,
																	),
																)
															: formatNumber(
																	value,
																)
												}}
												{{
													Object.values(
														EventConversionOffersRate,
													).includes(
														reward as EventConversionOffersRate,
													)
														? "%"
														: ""
												}}
											</span>

											<div
												v-if="
													reward ===
													'eventiumGameRewardIncome'
												"
												:class="`icon-upgrade-${EventUpgradeIds.ch_bow_saw}-20`"
											/>
											<div
												v-else-if="
													Object.values(
														EventAdditionalResource,
													).includes(
														reward as EventAdditionalResource,
													)
												"
												:class="
													eventChestKeyIconCssClass(
														20,
													)
												"
											/>
											<div
												v-else-if="
													reward ===
														'eventiumGameRewardConversion' ||
													Object.values(
														EventConversionOffersRate,
													).includes(
														reward as EventConversionOffersRate,
													)
												"
												class="icon-event-xrate-20"
											/>
											<div
												v-else-if="
													reward.includes('icon')
												"
												class="rewards-grid__icon"
												:class="`${reward}-20`"
											/>
											<div
												v-else-if="
													[
														'eventium-income',
														'extra-eventium-painted-egg-pickups',
														'extra-eventium-golden-egg-pickups',
														'extra-chance-spawn-golden-egg-pickup',
														'extra-eventium-pumpkin-pickups',
														'extra-eventium-jackolantern-pickups',
														'extra-chance-spawn-jackolantern-pickup',
													].includes(reward)
												"
												class="rewards-grid__icon"
												:class="`icon-${reward}-20`"
											/>
											<AppMainIcon
												v-else-if="
													isMultiplier(
														reward as ResourceType,
													)
												"
												class="claimable-reward__icon"
												:iconName="
													getMultiplierResourceType(
														reward as ResourceType,
													).toLowerCase()
												"
												:iconSize="20"
											/>
											<AppMainIcon
												v-else-if="
													Object.values(
														ResourceTypeEnum,
													).includes(
														reward as ResourceType,
													)
												"
												class="claimable-reward__icon"
												:iconName="reward.toLowerCase()"
												:iconSize="20"
											/>
											<div
												v-else-if="
													Object.values(
														UpgradeIds,
													).includes(+reward)
												"
												:class="`icon-upgrade-${reward}-20`"
											/>
											<div
												v-else-if="
													Object.values(
														FrameName,
													).includes(
														reward as FrameName,
													)
												"
												class="claimable-reward__image"
												:class="`icon-${reward.toLowerCase().replace('.png', '')}-20`"
											/>

											<template
												v-else-if="
													Object.values(
														OfferComplementaryReward,
													).includes(
														reward as OfferComplementaryReward,
													)
												"
											>
												<div
													v-if="
														reward ===
														OfferComplementaryReward.EVENT_PASS
													"
													class="claimable-reward__image-collage"
													:style="{
														backgroundImage: `url('${IMAGES_CDN_URL}/shop/backgrounds/templates/event-pass/${eventAttributes.type}/event-pass-reward-img.avif')`,
													}"
												/>
												<div
													v-if="
														reward ===
														OfferComplementaryReward.PROVISION
													"
													class="claimable-reward__image-collage"
													:style="{
														backgroundImage: `url('${IMAGES_CDN_URL}/shop/backgrounds/templates/royal-provision-reward-img.avif')`,
													}"
												/>
											</template>
										</div>
									</template>
								</div>
							</div>

							<div
								class="popup__actions"
								v-if="computedActions"
								:class="{
									'popup__actions--paddingforced':
										computedIsPaddingless,
								}"
							>
								<AppTextButton
									v-for="(action, index) in computedActions"
									:key="index"
									:type="action.buttonType"
									:text="action.label"
									:price="action.price"
									@click="runPopupAction(action)"
								/>
							</div>
						</template>
					</div>
				</div>
				<div
					v-if="computedHasOuterBar"
					class="outer-bar"
					id="popup-outer-bar-target"
				>
					<slot name="outer-bar">
						<ShopPurchaseButton
							v-if="offerData"
							:offer="offerData"
							:analytics="popupStore.state.value.analytics"
						/>
					</slot>
				</div>
			</div>
		</div>
	</teleport>
</template>

<script lang="ts" setup>
import AccountDetail from "@/components/AccountDetail.vue";
import AppIconButton from "@/components/AppIconButton.vue";
import AppMainIcon from "@/components/AppMainIcon.vue";
import AppPrice from "@/components/AppPrice.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import ChestPopupBody from "@/components/Chests/ChestPopupBody.vue";
import EventIntroPopup from "@/components/Event/EventIntroPopup.vue";
import HorizontalDivider from "@/components/HorizontalDivider.vue";
import ShopPurchaseButton from "@/components/shop/ShopPurchaseButton.vue";
import DividerLine from "@/components/SVG/DividerLine.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import RaggedBoxBackground from "@/components/SVG/RaggedBoxBackground.vue";
import RectangleBoxContainer from "@/components/SVG/RectangleBoxContainer.vue";
import { IMAGES_CDN_URL, TICK_DURATION } from "@/config/constants";
import { eventChestKeyIconCssClass, useEventStore } from "@/event";
import { FrameName } from "@/game/entities/config";
import type { ResourceType } from "@/game/entities/config/resources";
import { ResourceType as ResourceTypeEnum } from "@/game/entities/config/resources";
import { EventUpgradeIds, UpgradeIds } from "@/game/entities/config/upgrades/";
import { eventUpgrades } from "@/game/entities/config/upgrades/event";
import SoundService from "@/game/SoundService";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useRewardClaimGuard from "@/hooks/useRewardClaimGuard";
import useSocketService from "@/hooks/useSocketService";
import Communicator, { LinkTypes } from "@/libs/AppCommunicatior";
import type { ExtendedPiggyBankOffer } from "@/services/OfferProvider";
import { type MapExpansionConfig } from "@/store/useMapExpansionStore";
import type { PopupAction, PopupBodyData } from "@/store/usePopupStore";
import {
	activePopupComponent,
	activePopupProps,
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore from "@/store/useRuntimeStore";
import useSettingsStore from "@/store/useSettingsStore";
import useUserStore from "@/store/useUserStore";
import type { Rewards, UserData } from "@/types";
import type { MarketChestType } from "@/types/event.ts";
import type { ShopItem } from "@/types/shop";
import {
	EventAdditionalResource,
	EventConversionOffersRate,
	EventUpgrades,
	OfferComplementaryReward,
} from "@/types/shop";
import type { FiatCurrencies, News } from "@/types/ui";
import {
	EventCategory,
	FontWeights,
	IconTypes,
	ResourceChangeHighlight,
	SkinTypes,
	StatusTypes,
} from "@/types/ui";
import { formatNumber, formatPrice } from "@/utils/format";
import {
	getMultiplierResourceType,
	isMultiplier,
	multiplierValue,
} from "@/utils/shop/values.ts";
import { translate } from "@/utils/translate";
import { pathEvents, pathImages } from "@/utils/url";
import { storeToRefs } from "pinia";
import {
	computed,
	markRaw,
	nextTick,
	onMounted,
	ref,
	useTemplateRef,
	watch,
} from "vue";
import DailyRewardPopupBody from "./DailyReward/DailyRewardPopupBody.vue";
import EventBestReward from "./Event/EventBestReward.vue";
import EventPossibleRewardsPopupBody from "./Event/EventPossibleRewardsPopupBody.vue";
import ChangeNamePopupBody from "./Leaderboard/ChangeNamePopupBody.vue";
import LockedMapRegionPopupBody from "./Map/LockedMapRegionPopupBody.vue";
import NewsPopupBody from "./News/NewsPopupBody.vue";
import RulesPopupBody from "./Rules/RulesPopupBody.vue";
import QTPopupBody from "./Upgrades/QTPopupBody.vue";

type ComputedBodySpecial = Record<string, unknown>;

// All props optional for local use
interface Props {
	heading?: string;
	headingResource?: ResourceType;
	decoratedHeading?: string;
	body?: PopupBodyData;
	width?: number;
	height?: number | string | null;
	maxHeight?: number | null;
	skin?: (typeof SkinTypes)[keyof typeof SkinTypes];
	type?: (typeof PopupType)[keyof typeof PopupType];
	isPaddingless?: boolean | null;
	isCentered?: boolean | null;
	imgSrc?: string | null;
	spriteData?: Record<string, string | number | boolean> | null;
	visible?: boolean;
	closeable?: boolean;
	largeHeader?: boolean;
	percentageWidth?: string;
	isClickable?: boolean;
	hasOverlap?: boolean;
	hasOuterBar?: boolean;
	forceDividerLine?: boolean;
	rewards?: Rewards;
	customClassList?: string;
	scaleDelay?: number;
	maxZIndex?: boolean;
}

const {
	heading,
	headingResource,
	decoratedHeading,
	body = null,
	width,
	height = null,
	maxHeight = null,
	skin = null,
	type = null,
	isPaddingless = null,
	isCentered = null,
	imgSrc = null,
	spriteData = null,
	visible,
	closeable = null,
	largeHeader = null,
	percentageWidth,
	isClickable = true,
	hasOverlap = null,
	hasOuterBar = null,
	forceDividerLine = null,
	rewards = null,
	customClassList = null,
	scaleDelay = null,
	maxZIndex = null,
} = defineProps<Props>();

const MILESTONES_TAB_INDEX = 2;

const emit = defineEmits(["close", "scale"]);

const emitter = markRaw(useEmitter());
const socketService = markRaw(useSocketService());
const soundService = markRaw(SoundService.getInstance());
const claimGuard = useRewardClaimGuard();
const runningActions = new WeakSet<NonNullable<PopupAction["callback"]>>();
const runPopupAction = async (action: PopupAction): Promise<void> => {
	const callback = action.callback;
	if (!callback) return;
	if (runningActions.has(callback)) return;
	runningActions.add(callback);
	try {
		if (
			action.label === translate("button.buy") ||
			(action.label === translate("button.claim") &&
				usePopup.state.id === PopupDictionary.PAYMENT_SUCCESSFUL)
		) {
			if (!(await claimGuard.checkConnection())) return;
			if (
				!computedActions.value?.some(
					(current: PopupAction): boolean =>
						current.callback === callback,
				)
			)
				return;
		}
		await callback();
	} catch (error: unknown) {
		console.error("Popup action could not finish", error);
		emitter.emit(EmitterEvents.GameShowConnectionErrorPopup, {
			recoverable: true,
		});
	} finally {
		runningActions.delete(callback);
	}
};
const communicator = markRaw(Communicator.getInstance());

const userStore = useUserStore();
const usePopup = usePopupStore();
const popupStore = storeToRefs(usePopup);
const resourcesStore = useResourcesStore();

const settingsStore = useSettingsStore();
const confirmGemsMessages = ref<boolean>(false);

const eventStore = useEventStore();
const { isActive: isEventActive, attributes: eventAttributes } =
	storeToRefs(eventStore);

const shouldUseStoreState = computed((): boolean => visible === undefined);

const shouldShowPopup = computed((): boolean =>
	shouldUseStoreState.value
		? popupStore.isVisible.value === true
		: visible === true,
);

const computedHeading = computed(
	(): string => heading ?? popupStore.heading.value ?? false,
);

const computedHeadingResource = computed(
	(): ResourceType | null =>
		headingResource ?? popupStore.headingResource.value ?? null,
);

const computedDecoratedHeading = computed(
	(): string =>
		decoratedHeading ?? popupStore.decoratedHeading.value ?? false,
);

const computedPercentageWidth = computed(
	(): string => percentageWidth ?? popupStore.percentageWidth.value ?? false,
);

const computedBody = computed(
	(): PopupBodyData => body ?? popupStore.body.value ?? null,
);

const computedWidth = computed((): number =>
	width !== undefined ? width : (popupStore.width.value ?? 22.125),
);

const computedHeight = computed(
	(): number | string => height ?? popupStore.height.value ?? 9.5,
);

const computedMaxHeight = computed(
	(): number | null => maxHeight ?? popupStore.maxHeight.value ?? null,
);

const computedSkin = computed(
	(): SkinTypes => skin ?? popupStore.skin.value ?? SkinTypes.LIGHT,
);

const computedType = computed(
	(): PopupType => type ?? popupStore.type.value ?? null,
);

const computedIsPaddingless = computed(
	(): boolean => isPaddingless ?? popupStore.isPaddingless.value ?? false,
);

const computedIsCentered = computed(
	(): boolean => isCentered ?? popupStore.isCentered.value ?? false,
);

const computedMaxZIndex = computed(
	(): boolean => maxZIndex ?? popupStore.maxZIndex.value ?? false,
);

const computedImgSrc = computed(
	(): string | null => imgSrc ?? popupStore.imgSrc.value ?? null,
);

const computedSpriteData = computed(
	(): Record<string, string | number | boolean> | null =>
		spriteData ?? popupStore.spriteData.value ?? null,
);

const computedActions = computed(
	(): Array<PopupAction> | null => popupStore.actions.value ?? null,
);

const computedShowSkipMessageNotice = computed(
	(): boolean => popupStore.showSkipMessageNotice.value ?? null,
);

const computedScaleDelay = computed(
	(): number => scaleDelay ?? popupStore.scaleDelay.value ?? 0,
);

const computedForceDividerLine = computed(
	(): boolean =>
		forceDividerLine ?? popupStore.forceDividerLine.value ?? false,
);

const computedHasOverlap = computed(
	(): boolean => hasOverlap ?? popupStore.hasOverlap.value ?? false,
);

const computedHasOuterBar = computed(
	(): boolean => hasOuterBar ?? popupStore.hasOuterBar.value ?? false,
);

const computedLargeHeader = computed(
	(): boolean => largeHeader ?? popupStore.largeHeader.value ?? false,
);

const computedCloseable = computed(
	(): boolean => closeable ?? popupStore.closeable.value ?? true,
);

const offerData = computed(() => {
	return activePopupProps.value?.contentData as
		| ShopItem
		| ExtendedPiggyBankOffer
		| undefined;
});

const computedRewards = computed((): Rewards | null => {
	const rawRewards = rewards ?? popupStore.rewards.value ?? null;

	if (!rawRewards) return null;

	// Iterate over all key-value pairs to transform event conversion rate or other special phantom rewards
	const transformed = Object.entries(rawRewards).reduce(
		(
			acc: Record<string | number, string | number>,
			[key, value]: [string, number],
		): Record<string | number, string | number> => {
			const isUpgrade = Object.values(EventUpgrades).includes(
				key as EventUpgrades,
			);
			const isConversionRate = key.includes(
				"event_conversion_offers_rate_",
			);

			// Check if the key contains the conversion rate prefix
			if (isConversionRate) {
				// Extract numeric suffix and convert it to a number
				const value = +(key.match(/\d+/)?.[0] || 0);

				acc[key] = value;
			} else if (isUpgrade) {
				const upgradeData =
					eventUpgrades.get(EventUpgradeIds[key as EventUpgrades]) ??
					null;

				const upgradeDataAttributes = upgradeData?.attributes ?? null;

				if (!upgradeDataAttributes) acc[key] = value;

				if (upgradeDataAttributes?.income) {
					acc[`${upgradeDataAttributes?.icon ?? key}`] =
						`${upgradeDataAttributes.income}/${TICK_DURATION}s`;
				}

				if (upgradeDataAttributes?.percentage) {
					acc[`${upgradeDataAttributes?.icon ?? key}`] =
						`+${upgradeDataAttributes.percentage}%`;
				}

				if (upgradeDataAttributes?.value) {
					acc[`${upgradeDataAttributes?.icon ?? key}`] =
						`+${upgradeDataAttributes.value}`;
				}
			} else {
				// Keep standard keys like "gems" or "eventium" as they are
				acc[key] = value;
			}
			return acc;
		},
		{} as Record<string | number, number | string>,
	);

	return transformed as Rewards;
});

const isSpecialShopDetail = computed((): boolean => {
	return (
		(computedBody.value as ComputedBodySpecial)?.context === "shop" &&
		(computedBody?.value as ComputedBodySpecial)?.type === "special"
	);
});

const fiatPrice = computed((): Record<FiatCurrencies, number> | null => {
	return (
		((computedBody.value as ComputedBodySpecial)?.fiatPrice as Record<
			FiatCurrencies,
			number
		>) ?? null
	);
});

const motionVideoPath = computed((): string | null => {
	const videoEnabled = false;
	if (
		videoEnabled &&
		isEventActive.value &&
		computedType.value === PopupType.EVENT
	) {
		if (eventAttributes.value.type === EventCategory.CHRISTMAS) {
			return `${pathEvents}/christmas/motion/snowfall.mp4?v2`;
		}
	}
	return null;
});

const pathImagesHttp = computed((): string => {
	// zmení iba assets:// na začiatku
	return pathImages.replace(/^assets:\/\//, "https://");
});

const userDataExisting = ref<UserData | null>(null);
const userDataOverride = ref<UserData | null>(null);

const close = (): void => {
	usePopup.hidePopup();
};

const emitGenericClick = (): void => {
	soundService.playGenerickClick();
};

const linkAccount = (type?: LinkTypes): void => {
	emitGenericClick();
	communicator.linkAccount(
		type,
		useRuntimeStore().linkingAccountSocialMediaType,
	);
};

const setUsersData = async (userId: string): Promise<void> => {
	userDataOverride.value = {
		id: userStore.userId,
		name: userStore.userName,
		level: userStore.playerLevel,
		coinsIncome: resourcesStore.coinsIncome,
		tilesExplored: userStore.tilesExplored,
	};
	socketService.getUserById(userId).then((data: UserData): void => {
		if (data) {
			userDataExisting.value = {
				id: data.id,
				name: data.name,
				level: data.level,
				coinsIncome: data.coinsIncome,
				tilesExplored: data.tilesExplored,
			};
		} else {
			userDataExisting.value = null;
		}
	});
};

const openEventIntro = (): void => {
	usePopup.showPopup({
		id: PopupDictionary.EVENT_INTRO,
		type: PopupType.EVENT,
		width: 24.5,
		height: "auto",
		isPaddingless: true,
		skin: SkinTypes.DARK,
		isPriority: true,
		hasOverlap: true,
		hasOuterBar: true,
		component: markRaw(EventIntroPopup),
		componentProps: { initialStep: 1 },
	});
};

const containerRef = useTemplateRef<HTMLElement | null>("containerRef");

watch(
	(): PopupType => computedType.value,
	(newType: PopupType): void => {
		if (newType === PopupType.ACCOUNT_LINK_OPTIONS) {
			setUsersData(popupStore.body.value as string);
		}
	},
);

watch(
	(): boolean => confirmGemsMessages.value,
	(value: boolean): void => {
		if (computedHeading.value === translate("ui.premium_upgrade")) {
			settingsStore.setShowConfirmGemsMessagesUpgrades(!value);
		} else if (computedHeading.value === translate("ui.shop")) {
			settingsStore.setShowConfirmGemsMessagesShop(!value);
		} else {
			settingsStore.setShowConfirmGemsMessages(!value);
		}
	},
);
watch(
	(): boolean => shouldShowPopup.value,
	(value: boolean): void => {
		if (value) {
			confirmGemsMessages.value = false;
		}
	},
);

type OverflowInfo = {
	el: HTMLElement;
	overflow: number; // maximum overlap on a side of the bounding box
};

const popupScale = ref<number>(1);
const shrinkPopup = async (
	containerElement: HTMLElement | null,
	scaleDelay = 0,
): Promise<void> => {
	if (!containerElement) return;

	if (scaleDelay > 0) containerElement.style.opacity = "0";
	await nextTick();

	// 1) Temporarily cancel scale for correct measurement
	const prevTransform = containerElement.style.transform;
	containerElement.style.transform = "translate(-50%, -50%) scale(1)";

	const notAllowedClasses = ["scrollbar__content" /* scrollbar */];

	const nodes = Array.from(
		containerElement.querySelectorAll<HTMLElement>(
			// IMPORTANT: Select elements for overflow check, excluding disallowed classes
			// NOTE: .${c} = specific disallowed element + .${c} * = all elements inside disallowed element
			`*:not(${notAllowedClasses.map((c: string): string => `.${c}, .${c} *`).join(", ")}`,
		),
	).filter(
		// NOTE: include only visible elements (offsetParent !== null)
		// NOTE: but always keep the container itself
		(element: HTMLElement): boolean =>
			element.offsetParent !== null || element === containerElement,
	);

	if (nodes.length === 0) {
		containerElement.style.transform = prevTransform;
		containerElement.style.opacity = "1";
		return;
	}

	// 2) Find the "most overlapping" element + count the common box
	let minLeft = Infinity,
		minTop = Infinity,
		maxRight = -Infinity,
		maxBottom = -Infinity;

	let worst: OverflowInfo | null = null;

	for (const el of nodes) {
		const elName = el.localName.toLowerCase();

		const notAllowedTags = [
			"image",
			"g",
			"path",
			"rect",
			"circle",
			"ellipse",
			"line",
			"polyline",
			"polygon",
		];

		if (notAllowedTags.includes(elName)) {
			continue;
		}

		const r = el.getBoundingClientRect();

		minLeft = Math.min(minLeft, r.left);
		minTop = Math.min(minTop, r.top);
		maxRight = Math.max(maxRight, r.right);
		maxBottom = Math.max(maxBottom, r.bottom);

		const overflowLeft = Math.max(0, 0 - r.left);
		const overflowTop = Math.max(0, 0 - r.top);
		const overflowRight = Math.max(0, r.right - window.innerWidth);
		const overflowBottom = Math.max(0, r.bottom - window.innerHeight);
		const maxOverflow = Math.max(
			overflowLeft,
			overflowTop,
			overflowRight,
			overflowBottom,
		);

		if (
			(!worst && maxOverflow > 0) ||
			(worst && maxOverflow > worst.overflow)
		) {
			worst = { el, overflow: maxOverflow };
		}
	}

	// check if the popup has been scaled due to overflowing content
	// if so, log the element which caused the scaling and the amount of overflow
	// NOTE: this is for QA debugging, it should be removed when the popup is fixed
	// eslint-disable-next-line no-console
	console.log(
		"Popup overflowing?",
		worst ? "YES" : "NO",
		worst?.el,
		worst?.overflow,
	);

	if (!worst) {
		containerElement.style.opacity = "1";
		return;
	}

	// 3) Scale, which fits all content into the viewport
	const contentWidth = maxRight - minLeft;
	const contentHeight = maxBottom - minTop;

	// Safety margin (deducted from the resulting scale)
	const marginFactor = 0.05;

	const scaleX = contentWidth > 0 ? window.innerWidth / contentWidth : 1;
	const scaleY = contentHeight > 0 ? window.innerHeight / contentHeight : 1;

	let scale = Math.min(1, scaleX, scaleY);

	// Subtract the visual margin
	scale = Math.max(0, scale - marginFactor);

	// 4) Apply the resulting scale (preserve centering)
	if (scaleDelay > 0) {
		setTimeout((): void => {
			containerElement.style.transform = `translate(-50%, -50%) scale(${scale})`;
			emit("scale", scale);
			containerElement.style.opacity = "1";
		}, scaleDelay); // Required delay for the actual scrollbar; requestAnimationFrame or nextTick is not sufficient.
	} else {
		containerElement.style.transform = `translate(-50%, -50%) scale(${scale})`;
		emit("scale", scale);
	}

	containerElement.style.setProperty("--scale", scale.toString());
	popupScale.value = scale;
};

watch(
	(): HTMLElement | null => containerRef.value,
	async (containerElement: HTMLElement | null): Promise<void> => {
		shrinkPopup(containerElement, computedScaleDelay.value);
	},
	{ immediate: true },
);

const popupHeaderImgSize = computed((): number => {
	let width = (computedSpriteData.value?.size as number) ?? 416;

	if (type === PopupType.EVENT) {
		if (popupScale.value > 0.9 && popupScale.value <= 1) {
			width = (computedSpriteData.value?.size as number) ?? 416;
		} else if (popupScale.value <= 0.9 && popupScale.value > 0.85) {
			width = 380;
		} else {
			width = 360;
		}
	}
	return width;
});

// since emitter cannot directly call promise<void>, we wrapped it in a helper void function that calls it synchronously
const call = (): void => {
	if (containerRef.value) {
		shrinkPopup(containerRef.value);
	}
};

onMounted((): void => {
	emitter.on(EmitterEvents.SwitchPopupTab, call);
});
</script>

<style lang="scss" scoped>
.popup {
	@include flex-col-center;
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: 10;
	box-shadow: inset 0 0 0 0.125rem rgba(255, 255, 255, 0.1);
	border-radius: 0.15rem;
	background: url($path-images + "backgrounds/textures/window-texture-bg.avif")
		repeat center;
	background-size: 90% auto;
	padding: 0.125rem;
	width: 22.125rem;
	min-height: 9.5rem;

	&--transparent,
	&.popup--overlapped {
		box-shadow: unset;
		background: transparent;
		padding: 0;

		.body {
			box-shadow: none !important;
		}

		.body,
		.body-content {
			overflow: visible;
		}
	}

	&--event {
		box-shadow: inset 0 0 0 0.125rem rgb(218, 171, 44);
		&[data-event-type="fishing"] {
			&:not(.popup--overlapped) {
				box-shadow: inset 0 0 0 0.125rem rgba(7, 98, 131, 1);
			}
		}

		// Important fix for event popups (handles duplicate scrollbars)
		:deep(.tabs) {
			flex: 1 1 auto;
			min-height: 0;

			&.tabs--event-main,
			&.tabs--event-shop {
				.scrollbar__wrapper {
					.scroll-fade {
						flex-direction: column;
					}
				}
			}

			.scroll-fade-container.scroll-fade-container--tabs {
				flex: 1 1 auto;
				min-height: 0;

				.scrollbar__wrapper {
					display: flex;
					flex: 1 1 auto;
					height: auto;
					min-height: 0;

					.scroll-fade {
						display: flex;
						flex-direction: column;
						width: 100%;

						.scrollbar__content {
							display: flex;
							min-height: 0;

							.event-milestones {
								display: flex;
								flex-direction: column;
								width: 100%;
								min-height: 0;

								.event-milestones__info,
								.event-milestones__total {
									flex-shrink: 0;
								}

								.scroll-fade-container {
									display: flex;
									flex: 1 1 auto;
									min-height: 0;
								}
							}
						}
					}
				}
			}
		}
		:deep(.scrollbar__thumbPlaceholder--horizontal) {
			opacity: 0 !important;
		}
	}

	&__sprite {
		// NOTE due to the sprite’s fixed size, which overflows on smaller devices, this serves as a clipping mask centered via flex
		width: 100%;
		@include flex-col-center;
		position: relative;
		overflow: hidden;

		&-img {
			object-fit: cover;
		}

		&--overlapped {
			overflow-x: clip;
			overflow-y: visible;
		}

		&--christmas-event,
		&--easter-event,
		&--fishing-event,
		&--firefest-event,
		&--solarfair-event,
		&--hunting-event,
		&--halloween-event,
		&--pirates-event {
			&.popup__sprite {
				&--416 {
					margin-top: -1rem;
				}

				&--380 {
					margin-top: -0.875rem;
				}

				&--360 {
					margin-top: -0.813rem;
				}
			}
		}
	}

	&-wrapper {
		position: absolute;
		top: 0;
		left: 0;
		z-index: 9;
		width: 100%;
		height: 100%;
		overflow: hidden;
		&.max-z-index {
			z-index: 9999;
		}

		&::before {
			position: absolute;
			top: 0;
			left: 0;
			background: rgba(0, 0, 0, 0.9);
			width: 100%;
			height: 100%;
			content: "";
		}
	}

	.decorated-header {
		position: absolute;
		top: -3rem;
		@include flex-row-center;
		padding-bottom: 0.25rem;
		width: 25rem;
		height: 2.25rem;
		color: rgb(244, 210, 120);
		font-weight: 800;
		font-size: 1rem;
		text-shadow: 0.04375rem 0.04375rem 0.0625rem rgba(0, 0, 0, 0.6);
		text-transform: uppercase;
		@include background(
			url($path-images + "popups/level-header-bg.avif?v2"),
			contain,
			center,
			no-repeat
		);
	}

	.header {
		@include flex-row-between;
		padding: 0.25rem;
		width: 100%;

		&--has-img {
			@include flex-row-start;
		}

		&--fixed-top {
			position: absolute;
			top: 0;
			left: 0;
			padding: 0.375rem;
			width: 100%;
		}

		&--large {
			align-items: start;
			height: 6.5rem;
		}

		.heading {
			padding-bottom: 0.25rem;
			padding-left: 0.75rem;
			color: #f4d278;
			font-weight: 800;
			font-size: 1.25rem;
			text-shadow: 0.044rem 0.044rem 0.0625rem rgba(0, 0, 0, 0.6);
			text-transform: uppercase;

			&--center {
				flex: 1;
				padding-right: 0;
				padding-left: 0;
				text-align: center;
			}
		}

		.icon--has-img {
			position: absolute;
			top: 0.5rem;
			right: 0.5rem;
		}
	}

	&--light {
		.body {
			background-image: linear-gradient(to top, #d7bd8d, #f2dcb5);

			&::before {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 1 !important;
				// NOTE: Multiply blending is better for light colors
				mix-blend-mode: multiply !important;
				background: url($path-images + "backgrounds/textures/marble-texture-bg.avif")
					repeat center;
				background-size: 40%;
				width: 100%;
				height: 100%;
				content: "";
			}
		}
	}

	&.compensation-box-popup {
		.body .body-content {
			padding: 0.75rem;
		}
	}

	.body {
		display: flex;
		position: relative;
		flex: 1 1 auto;
		flex-direction: column;
		box-shadow: inset 0 0 0.75rem 0 rgba(49, 37, 22, 0.6);
		width: 100%;
		overflow: hidden;

		&-content {
			display: flex;
			position: relative;
			flex: 1 1 auto;
			flex-direction: column;
			z-index: 1;
			padding: 1.625rem;
			overflow-x: hidden;
			overflow-y: auto;
			font-size: 0.906rem;

			&--paddingless {
				// padding: 0.5rem 0;
				padding: 0;
			}

			&--center {
				justify-content: center;
				align-items: center;
				text-align: center;
			}

			&--rate-us {
				color: rgb(55, 38, 21);
				font-size: 1rem;
			}

			.rate-us {
				@include flex-col-center;
				gap: 0.5rem;
				font-size: 1rem;

				&__stars {
					display: flex;
					gap: 0.25rem;
				}

				&__subheader {
					color: rgb(55, 38, 21);
					font-weight: 800;
					font-size: 1rem;
				}
			}
			.discord-reward {
				@include flex-col-center;
				gap: 0.5rem;
				padding: 0 1rem;
				font-size: 1rem;

				&__subheader {
					color: rgb(55, 38, 21);
					font-weight: 800;
					font-size: 1rem;
				}
			}
			&--delete {
				color: rgb(207, 0, 50);
				.delete-subheader {
					font-weight: 800;
					font-size: 1rem;
				}
			}
			&--acc-link-options {
				justify-content: space-between;
				padding: 0;

				&--content-wrapper {
					width: 100%;
					height: 100%;
					@include flex-col-evenly;
					padding: 0 1.625rem;

					.sub-header {
						font-weight: 800;
					}
				}

				.link-account-warning {
					box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
					border: solid 0.05rem rgb(207, 0, 50);
					background-color: rgb(64, 22, 34);
					width: 100%;
					height: 2rem;
					color: rgb(207, 0, 50);
					font-weight: 800;
					font-size: 0.8125rem;
					text-transform: uppercase;
					@include flex-row-center;
				}
			}
		}

		.premium-box {
			flex: 1 1 auto;
			width: 100%;
			@include flex-row-center;
			position: relative;
			box-shadow: inset 0 0 0.75rem 0 rgba(29, 30, 33, 0.8);
			background-image: linear-gradient(to top, #25112e, #704a85);

			&::before {
				position: absolute;
				top: 0;
				left: 0;
				opacity: 0.6;
				mix-blend-mode: color-burn;
				@include background(
					url($path-images + "backgrounds/textures/marble-texture-bg.avif"),
					150%,
					right top,
					repeat
				);
				width: 100%;
				height: 100%;
				content: "";
			}

			&__content {
				position: relative;
				top: 0;
				left: 0;
				gap: 0.625rem;
				width: 100%;
				height: 100%;
				@include flex-col-center;

				&--special-offer {
					&::before {
						position: absolute;
						top: 0;
						left: 0;
						width: 100%;
						height: 100%;
						content: "";
						@include background(
							url($path-images + "shop/backgrounds/offer-purple-castle-bg.avif"),
							cover,
							top left,
							no-repeat
						);
						z-index: 0;
					}

					& > * {
						z-index: 1;
					}
				}
			}

			&__text {
				margin-top: 0.5rem;
				color: #f2dcb5;
				font-size: 0.875rem;
			}

			&__text2 {
				color: #fff3dd;
				font-weight: bold;
				font-size: 1rem;
				text-transform: uppercase;
			}

			&__price {
				mask-image: linear-gradient(
					to right,
					transparent 2%,
					black 20%,
					black 80%,
					transparent 98%
				);
				border-width: 0.063rem;
				border-style: solid;
				border-image-source: linear-gradient(
					to top,
					#ab51db,
					rgba(171, 81, 219, 0.8),
					rgba(171, 81, 219, 0.8),
					#ab51db
				);
				border-image-slice: 1;
				background-image: linear-gradient(
					to top,
					#661094,
					rgba(102, 16, 148, 0.8),
					rgba(102, 16, 148, 0.8),
					#661094
				);
				width: 60%;
				height: 2.25rem;
				@include flex-row-center;
				margin-bottom: 1rem;

				&--outer {
					gap: 0.375rem;
					margin-top: 0.375rem;
					width: 100%;
					@include flex-col-center;

					.price-label {
						color: #f2dcb5;
						font-size: 0.75rem;
						text-align: center;
						text-shadow: 0.044rem 0.044rem 0.063rem
							rgba(0, 0, 0, 0.6);
					}
				}

				.fiat-price {
					color: #fff3dd;
					font-weight: 800;
					font-size: 1.5rem;
					text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
				}

				.fiat-price,
				:deep(.price span) {
					padding-bottom: 0.25rem;
				}
			}
		}
	}

	.outer-bar {
		position: relative;
		margin-top: 2.125rem;
		@include flex-col-center;

		:deep(.special-building-pack-popup__action-bar) {
			gap: 1rem;
		}

		:deep(.pack-overview-popup__origin-price),
		:deep(.special-building-pack-popup__origin-price) {
			position: relative;
			color: #f4d278cc;
			font-size: 1rem;
			@include flex-row-center;
			align-items: baseline;
			gap: 0.25rem;
			background-image: linear-gradient(
				to right,
				rgba(85, 0, 20, 0) 0%,
				rgba(85, 0, 20, 0.8) 10%,
				rgba(85, 0, 20, 0.8) 90%,
				rgba(85, 0, 20, 0) 100%
			);
			padding: 0.125rem 2rem 0.25rem;

			&::before,
			&::after {
				position: absolute;
				left: 0;
				background: linear-gradient(
					to right,
					rgba(207, 0, 50, 0) 5%,
					rgba(207, 0, 50, 0.8) 20%,
					rgba(207, 0, 50, 0.8) 80%,
					rgba(207, 0, 50, 0) 95%
				);
				width: 100%;
				height: 0.05rem;
				content: "";
			}

			&::before {
				top: 0;
			}

			&::after {
				bottom: 0;
			}

			.price__value {
				position: relative;
				color: #f4d278cc;
				&::after {
					position: absolute;
					top: 50%;
					left: 50%;
					transform: translate(-50%, -50%) skewY(-21deg);
					background-color: #fff3dd;
					width: 100%;
					height: 0.063rem;
					content: "";
				}
			}
		}
		:deep(.pack-overview-popup__price),
		:deep(.special-building-pack-popup__price) {
			gap: 0.5rem;
			width: 18.5rem;
			height: 3rem;

			.text-button--inner .text-button__text-container {
				padding-bottom: 0.5rem;
				width: unset;
			}

			.dual-price {
				opacity: 0.9;
				color: #f2dcb5;
				font-size: 1rem;
			}
		}

		:deep(.pack-overview-popup__limit),
		:deep(.special-building-pack-popup__limit) {
			color: #fff;
			font-size: 0.75rem;
			text-align: center;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}
	}

	.horizontal-divider {
		width: calc(100% - 0.5rem);

		svg {
			width: 100%;
			height: auto;
		}
	}
	&__img {
		width: 100%;
		pointer-events: none;
	}

	&__actions {
		width: 100%;
		@include flex-row-center;
		gap: 0.375rem;
		margin: 0.875rem auto 0.625rem;

		.text-button {
			text-transform: uppercase;

			&:first-child {
				flex: 2 1 0;
			}
			&:not(:first-child) {
				flex: 1 1 0;
			}
		}

		&--paddingforced {
			padding: 0 0.875rem;
		}
	}

	&__skip-message-notice {
		display: flex;
		flex-direction: column;
		margin-top: 0.5rem;
		margin-left: 1.8rem;
		width: 100%;
		color: #fff3dd;
		line-height: 1rem;
		&__row {
			display: flex;
			align-items: center;
		}
		&__info {
			padding-left: 1.35rem;
			width: 100%;
			color: #f2dcb5;
			font-size: 0.688rem;
			text-align: left;
		}
		input[type="checkbox"] {
			margin-right: 0.35rem;
		}
		label {
			cursor: pointer;
			margin-bottom: 0.25rem;
			font-size: 0.9rem;
			text-align: left;
		}
	}

	&--premium {
		&::before {
			position: absolute;
			top: 0;
			left: 0;
			z-index: 0;
			background: rgba(49, 32, 55, 0.6);
			width: 100%;
			height: 100%;
			content: "";
		}

		& > div {
			position: relative;
			z-index: 1;
		}

		.body-content {
			justify-content: flex-start;
		}
	}

	.body-content--rate-us {
		padding: 0 0.875rem;
		.popup__actions {
			.text-button {
				text-transform: none;

				&:first-child {
					flex: 1 1 0;
				}
				&:not(:first-child) {
					flex: 2 1 0;
				}
			}

			&--paddingforced {
				padding: 0;
			}
		}
	}
	.body-content--discord-reward {
		padding: 0 0.875rem;
		.popup__actions {
			.text-button {
				text-transform: none;

				&:first-child {
					flex: 1 1 0;
				}
				&:not(:first-child) {
					flex: 2 1 0;
				}
			}

			&--paddingforced {
				padding: 0;
			}
		}
	}
	.body-content--delete {
		.body-content--texts {
			@include flex-col-center;
			flex: 1;
			gap: 0.5rem;
			margin-top: 0.5rem;
		}
		.popup__actions {
			.text-button {
				text-transform: none;

				&:first-child {
					flex: 1;
				}
				&:not(:first-child) {
					flex: 1;
				}
			}
		}
	}
	.body-content--upgrade {
		.body-content--texts {
			padding-top: 0.5rem;
		}
		.upgrade__subheader {
			color: rgb(55, 38, 21);
			font-weight: 800;
			font-size: 1rem;
		}
		.popup__actions {
			.text-button {
				text-transform: none;

				&:first-child {
					flex: 1;
				}
				&:not(:first-child) {
					flex: 1;
				}
			}
		}
	}
	.header-top-resource {
		@include flex-row-center;
		position: absolute;
		top: -4.3rem;
		left: 50%;
		transform: translateX(-50%);
		width: 11.25rem;
		height: 5.55rem;
		@include background(
			url($path-images + "popups/popup-header-resource-bg-circular.avif"),
			contain,
			center,
			no-repeat
		);
	}
}

.claimable-rewards-wrapper {
	position: relative;
	padding: 1rem 0.75rem;
	min-height: 7.125rem;
	@include flex-col-center;
	margin: 1rem;
	width: 100%;

	.claimable-rewards {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 0.5rem;
		width: 80%;

		.claimable-reward {
			@include flex-row-center;
			gap: 0.15rem;
			&__value {
				color: #372615;
				font-weight: bold;
				font-size: 1.063rem;
			}

			&__image-collage {
				position: relative;
				background-position: center;
				background-size: cover;
				background-repeat: no-repeat;
				width: 13rem;
				height: 10rem;
			}
		}
	}

	.ragged-box-bg {
		position: absolute;
		z-index: -1;
		filter: drop-shadow(0 0 0.1rem rgba(49, 37, 22, 0.6));
		width: 100%;
		height: 100%;
	}
}

.video-footage-wrapper {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;

	.video-footage {
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0.7;
		z-index: 0;
		width: 100%;
		height: 100%;
		// Full screen video setting regardless of its resolution
		object-fit: cover;
	}

	&--event {
		.video-footage {
			opacity: 1;
			mix-blend-mode: screen;
		}
	}
}

.event-popup__intro-btn {
	position: absolute;
	top: 2.75rem;
	left: 1rem;
	box-shadow: inset 0 0 0.5rem 0 rgba(0, 0, 0, 0.3);
	border-width: 0.075rem;
	border-style: solid;
	border-image-source: linear-gradient(to top, #bd9032, #f0d386);
	border-image-slice: 1;
	background-image: linear-gradient(to top, #181a1d, #303239);
	width: 1.75rem;
	height: 1.75rem;
	@include clickable-element;
	z-index: 9;

	&::before {
		position: absolute;
		top: 50%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 1rem;
		height: 1rem;
		content: "";
	}
	&[data-event-type="christmas"]::before {
		@include background(
			url($path-events + "christmas/icons/toy-beige-40.avif"),
			contain,
			center,
			no-repeat
		);
	}
	&[data-event-type="easter"]::before {
		@include background(
			url($path-events + "easter/icons/ico-rabbit-beige-40.avif"),
			contain,
			center,
			no-repeat
		);
	}
	&[data-event-type="fishing"]::before {
		@include background(
			url($path-events + "fishing/icons/ico-hook-beige-40.avif"),
			contain,
			center,
			no-repeat
		);
	}
	&[data-event-type="firefest"] {
		&::before {
			@include background(
				url($path-events + "firefest/icons/ico-fire-beige-40.avif"),
				contain,
				center,
				no-repeat
			);
		}
	}
	&[data-event-type="solarfair"] {
		&::before {
			@include background(
				url($path-events + "solarfair/icons/ico-sun-beige-40.avif"),
				contain,
				center,
				no-repeat
			);
		}
	}
	&[data-event-type="hunting"]::before {
		@include background(
			url($path-events + "hunting/icons/ico_bow-and-arrow-beige-40.avif"),
			contain,
			center,
			no-repeat
		);
	}
	&[data-event-type="halloween"]::before {
		@include background(
			url($path-events + "halloween/icons/ico-pumpkin-beige-40.avif"),
			contain,
			center,
			no-repeat
		);
	}
	&[data-event-type="pirates"]::before {
		@include background(
			url($path-events + "pirates/icons/ico-skull-beige-40.avif"),
			contain,
			center,
			no-repeat
		);
	}
}
</style>
